<div class="p-3 aco">
  <div class="d-flex align-items-center flex-wrap gap-2 mb-2">
    <h4 class="mb-0">Agent Commission Overview</h4>
    <span class="badge {{ modeBadgeClass }}" *ngIf="config">{{ modeLabel }}</span>
  </div>
  <p class="text-muted small mb-3">
    One reconciled view of agent commission for the period: what the engine <strong>computed</strong>,
    what was actually <strong>paid</strong>, any manual <strong>adjustments</strong> (clawbacks / goodwill /
    corrections), the separate <strong>referral</strong> wallet, the agent's <strong>live balance</strong>, and entry counts.
  </p>

  <!-- Config health -->
  <div class="alert alert-warning py-2 mb-3" *ngIf="configWarnings.length">
    <div *ngFor="let w of configWarnings"><i class="bi bi-exclamation-triangle"></i> {{ w }}</div>
  </div>

  <!-- Filter -->
  <div class="aco-filter mb-3">
    <div class="aco-filter__field aco-filter__field--period">
      <label class="form-label fw-bolder mb-1">Period</label>
      <select class="form-select form-select-sm" [ngModel]="selectedPeriodKey" (ngModelChange)="onPeriodChange($event)">
        <option *ngFor="let o of periodOptions" [value]="o.key">{{ o.label }} ({{ o.from_date }} - {{ o.to_date }})</option>
        <option value="custom">Custom Range</option>
      </select>
    </div>
    <div class="aco-filter__field">
      <label class="form-label fw-bolder mb-1">From</label>
      <input type="date" class="form-control form-control-sm" [(ngModel)]="filter.from_date" (ngModelChange)="selectedPeriodKey = 'custom'">
    </div>
    <div class="aco-filter__field">
      <label class="form-label fw-bolder mb-1">To</label>
      <input type="date" class="form-control form-control-sm" [(ngModel)]="filter.to_date" (ngModelChange)="selectedPeriodKey = 'custom'">
    </div>
    <div class="aco-filter__actions">
      <button type="button" class="btn btn-sm btn-primary" (click)="search_()" [disabled]="loading || !filter.from_date || !filter.to_date">Search</button>
      <button type="button" class="btn btn-sm btn-outline-secondary" (click)="reset()" [disabled]="loading">Reset</button>
    </div>
  </div>

  <div *ngIf="loading" class="text-muted">Loading…</div>

  <!-- Summary cards -->
  <div class="aco-cards mb-3" *ngIf="!loading && summary">
    <div class="aco-card">
      <div class="aco-card__label">Computed (engine)</div>
      <div class="aco-card__value">{{ summary.computed_total | number:'1.2-2' }}</div>
      <div class="aco-card__sub">{{ summary.report_rows_total }} report rows</div>
    </div>
    <div class="aco-card aco-card--paid">
      <div class="aco-card__label">Paid (commission wallet)</div>
      <div class="aco-card__value">{{ summary.paid_total | number:'1.2-2' }}</div>
      <div class="aco-card__sub">{{ summary.paid_count_total }} payouts</div>
    </div>
    <div class="aco-card" [class.aco-card--warn]="summary.adjustment_total != 0">
      <div class="aco-card__label">Manual Adjustments</div>
      <div class="aco-card__value">{{ summary.adjustment_total | number:'1.2-2' }}</div>
      <div class="aco-card__sub">clawback / goodwill / fix</div>
    </div>
    <div class="aco-card aco-card--highlight">
      <div class="aco-card__label">Net Paid Out</div>
      <div class="aco-card__value">{{ summary.net_paid_total | number:'1.2-2' }}</div>
      <div class="aco-card__sub">paid + adjustments</div>
    </div>
    <div class="aco-card">
      <div class="aco-card__label">Referral Wallet</div>
      <div class="aco-card__value">{{ summary.referral_total | number:'1.2-2' }}</div>
      <div class="aco-card__sub">{{ summary.referral_count_total }} entries</div>
    </div>
    <div class="aco-card">
      <div class="aco-card__label">Earning Agents</div>
      <div class="aco-card__value">{{ summary.earning_agents }} / {{ summary.agents }}</div>
      <div class="aco-card__sub">net paid &gt; 0</div>
    </div>
    <div class="aco-card" [class.aco-card--danger]="summary.negative_balance_agents > 0">
      <div class="aco-card__label">Negative Balances</div>
      <div class="aco-card__value">{{ summary.negative_balance_agents }}</div>
      <div class="aco-card__sub">{{ summary.negative_balance_total | number:'1.2-2' }}</div>
    </div>
  </div>

  <!-- Reconciliation banner -->
  <div class="alert py-2 mb-3" *ngIf="!loading && summary"
       [class.alert-success]="summary.reconciled" [class.alert-danger]="!summary.reconciled">
    <ng-container *ngIf="summary.reconciled">
      <i class="bi bi-check-circle"></i> <strong>Reconciled.</strong>
      Computed and paid commission tally for this period (within rounding).
    </ng-container>
    <ng-container *ngIf="!summary.reconciled">
      <i class="bi bi-exclamation-octagon"></i> <strong>Mismatch:</strong>
      computed vs paid differ by <strong>{{ summary.computed_vs_paid_delta | number:'1.2-2' }}</strong>
      across <strong>{{ summary.mismatch_agents }}</strong> agent(s). Use the “Mismatch” filter below to see which.
    </ng-container>
  </div>

  <!-- View filters -->
  <div class="d-flex align-items-center flex-wrap gap-2 mb-2" *ngIf="!loading && rows.length">
    <div class="btn-group btn-group-sm" role="group">
      <button class="btn" [class.btn-primary]="viewMode==='all'" [class.btn-outline-primary]="viewMode!=='all'" (click)="viewMode='all'">All ({{ rows.length }})</button>
      <button class="btn" [class.btn-primary]="viewMode==='earning'" [class.btn-outline-primary]="viewMode!=='earning'" (click)="viewMode='earning'">Earning ({{ summary?.earning_agents }})</button>
      <button class="btn" [class.btn-danger]="viewMode==='mismatch'" [class.btn-outline-danger]="viewMode!=='mismatch'" (click)="viewMode='mismatch'">Mismatch ({{ summary?.mismatch_agents }})</button>
      <button class="btn" [class.btn-danger]="viewMode==='negative'" [class.btn-outline-danger]="viewMode!=='negative'" (click)="viewMode='negative'">Negative bal ({{ summary?.negative_balance_agents }})</button>
    </div>
    <input type="text" class="form-control form-control-sm aco-search" placeholder="Search code / name…" [(ngModel)]="search">
  </div>

  <!-- Table -->
  <div class="table-responsive" *ngIf="!loading && filteredRows.length > 0">
    <table class="table table-sm table-striped align-middle aco-table">
      <thead class="table-dark">
        <tr>
          <th>Agent</th>
          <th>Name</th>
          <th class="text-end">Rate</th>
          <th class="text-end">Computed</th>
          <th class="text-end">Paid</th>
          <th class="text-end">Δ C−P</th>
          <th class="text-end">Adjust</th>
          <th class="text-end">Referral</th>
          <th class="text-end">Live Bal</th>
          <th class="text-end">Cnt</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <tr *ngFor="let r of filteredRows" [class.table-warning]="!r.reconciled" [class.aco-neg]="r.negative_balance">
          <td>
            <a *ngIf="r.player_uuid; else plainCode" href="javascript:void(0)" (click)="openMember(r)" title="Open wallet topup/withdraw">{{ r.agent_code }}</a>
            <ng-template #plainCode>{{ r.agent_code }}</ng-template>
          </td>
          <td class="text-truncate" style="max-width:160px">{{ r.agent_name }}</td>
          <td class="text-end">{{ (r.rate * 100) | number:'1.0-2' }}%</td>
          <td class="text-end">{{ r.computed_commission | number:'1.2-2' }}</td>
          <td class="text-end fw-bold">{{ r.paid_commission | number:'1.2-2' }}</td>
          <td class="text-end" [class.text-danger]="!r.reconciled">
            <span *ngIf="r.reconciled" class="text-success" title="reconciled">✓</span>
            <span *ngIf="!r.reconciled">{{ r.computed_vs_paid_delta | number:'1.2-2' }}</span>
          </td>
          <td class="text-end" [class.text-danger]="r.adjustment_amount < 0" [class.text-success]="r.adjustment_amount > 0">
            {{ r.adjustment_amount ? (r.adjustment_amount | number:'1.2-2') : '—' }}
          </td>
          <td class="text-end">{{ r.referral_paid ? (r.referral_paid | number:'1.2-2') : '—' }}</td>
          <td class="text-end" [class.text-danger]="r.negative_balance">
            {{ r.current_balance === null ? '—' : (r.current_balance | number:'1.2-2') }}
          </td>
          <td class="text-end text-muted small">{{ r.paid_count }}</td>
          <td class="text-end"><button class="btn btn-sm btn-outline-primary" (click)="drill(r)">View</button></td>
        </tr>
      </tbody>
    </table>
  </div>

  <div *ngIf="!loading && filteredRows.length === 0 && rows.length > 0" class="text-muted">No agents match this filter.</div>
  <div *ngIf="!loading && rows.length === 0" class="text-muted">No commission activity for this company in the selected period.</div>

  <!-- Legend -->
  <div class="aco-legend text-muted small mt-3" *ngIf="!loading && rows.length">
    <strong>How to read this:</strong>
    <span><b>Computed</b> = engine output (agent_report).</span>
    <span><b>Paid</b> = actual commission credited (agent_wallet).</span>
    <span><b>Δ C−P</b> = computed minus paid; ✓ means it tallies.</span>
    <span><b>Adjust</b> = manual clawback/goodwill/fix (excluded from Δ).</span>
    <span><b>Referral</b> = separate referral/red-share wallet.</span>
    <span><b>Live Bal</b> = current commission-wallet balance (red = negative).</span>
  </div>
</div>
